import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Award, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { TRAIN } from "@/constants/testIds";

export default function Certification() {
  const [search] = useSearchParams();
  const requestedTrack = search.get("track");
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const refreshMe = async () => {
    try { const { data } = await api.get("/training/certification/me"); setMe(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { refreshMe(); }, []);

  const startAttempt = async (trackKey) => {
    setErr(""); setResult(null); setBusy(true);
    try { const { data } = await api.post(`/training/certification/${trackKey}/start`); setAttempt(data); setAnswers({}); }
    catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (requestedTrack && !attempt && !result) startAttempt(requestedTrack); /* eslint-disable-line */ }, [requestedTrack]);

  const submit = async () => {
    if (!attempt) return;
    const arr = attempt.questions.map((_, i) => answers[i] ?? 0);
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/training/certification/attempts/${attempt.attempt_id}/submit`, { answers: arr });
      setResult(data); setAttempt(null); await refreshMe();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (attempt) {
    return (
      <div className="space-y-6 max-w-3xl">
        <button onClick={() => { setAttempt(null); setAnswers({}); }} className="btn-ghost !text-[0.7rem]">
          <ArrowLeft className="h-3 w-3" /> Cancel attempt
        </button>
        <header className="fade-up">
          <div className="eyebrow">Certification · {attempt.track_key.toUpperCase()}</div>
          <h1 className="headline-xl">Sit the test</h1>
          <p className="text-lifted-dim mt-2">Passing score: {attempt.passing_score}%</p>
        </header>
        <ol className="space-y-4">
          {attempt.questions.map((q, qi) => (
            <li key={qi} className="glass-panel p-5">
              <div className="font-bebas tracking-[0.14em] text-lifted-green text-xs mb-1">Q{qi + 1}</div>
              <div className="font-anton text-lg uppercase">{q.q}</div>
              <div className="mt-3 space-y-2">
                {q.choices.map((c, ci) => (
                  <label key={ci} className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    answers[qi] === ci ? "border-lifted-green bg-lifted-green/10" : "border-white/10 hover:bg-white/[0.03]"
                  }`}>
                    <input type="radio" name={`q-${qi}`} className="accent-lime-400" checked={answers[qi] === ci}
                           onChange={() => setAnswers({ ...answers, [qi]: ci })} />
                    <span className="text-sm text-lifted-text">{c}</span>
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ol>
        {err && <div className="text-rose-300 text-sm">{err}</div>}
        <button data-testid={TRAIN.certSubmitBtn} onClick={submit} disabled={busy} className="btn-lifted">
          {busy ? "Submitting…" : "Submit certification"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Lifted Academy</div>
        <h1 className="headline-xl">Certifications</h1>
        <p className="text-lifted-dim mt-2 max-w-2xl">Sit a track certification or review credentials you've already earned.</p>
      </header>

      {err && <div className="glass-panel p-4 text-rose-300 text-sm">{err}</div>}

      {result && (
        <section className={`glass-panel p-6 ${result.passed ? "border-lifted-green/40" : "border-rose-500/40"}`}>
          <div className="flex items-center gap-3">
            {result.passed ? <Award className="h-6 w-6 text-lifted-green" /> : <ShieldCheck className="h-6 w-6 text-rose-300" />}
            <div>
              <div data-testid={TRAIN.certPassed} className="font-anton text-2xl uppercase">
                {result.passed ? "Certification passed" : "Not yet — try again"}
              </div>
              <div className="text-sm text-lifted-dim">
                Score: <span data-testid={TRAIN.certScore} className="text-lifted-text">{result.score}</span> · Passing: {result.passing_score}
              </div>
              {result.certification && (
                <div className="text-xs text-lifted-mute mt-1">
                  Verification code: <code data-testid={TRAIN.certVerificationCode} className="text-lifted-green">{result.certification.verification_code}</code>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim mb-3">My certifications</div>
        {!me ? <div className="text-lifted-dim text-sm">Loading…</div>
          : me.certifications.length === 0 ? <div className="text-lifted-mute text-sm">No certifications yet.</div>
          : (
            <ul className="space-y-3">
              {me.certifications.map((c) => (
                <li key={c.id} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-b-0">
                  <div>
                    <div className="font-anton text-lg uppercase">{c.track_key} certification</div>
                    <div className="text-xs text-lifted-dim">Issued {c.issued_at} · Score {c.score}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <code className="text-lifted-green text-xs">{c.verification_code}</code>
                    <Link to={`/verify/${c.verification_code}`} target="_blank" rel="noreferrer" className="btn-ghost !text-[0.7rem] !py-1.5 !px-3">Verify</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["affiliate", "driver", "partner", "franchise"].map((k) => (
          <button key={k} data-testid={TRAIN.certStartBtn(k)} onClick={() => startAttempt(k)}
                  className="glass-panel p-5 text-left hover:-translate-y-1 hover:border-lifted-green/30 transition-transform">
            <div className="font-anton text-lg uppercase">{k} certification</div>
            <div className="text-xs text-lifted-dim mt-1">Tap to start an attempt. You must have completed the modules.</div>
          </button>
        ))}
      </section>

      <Link to="/training" className="btn-ghost inline-flex">← Back to training hub</Link>
    </div>
  );
}
